import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import DialogWithEnterAction from '../components/DialogWithEnterAction';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';

import { useTranslation } from 'react-i18next';

import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../Hooks/authHook';
import { Paper } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { statusColors } from '../Constants/constants';

import { TotpInput } from '../FormWidgets/TotpInput';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2)
    },
    '& .MuiTextField-root': {
      marginBottom: theme.spacing(2)
    }
  },
  loginActions: {
    display: 'flex',
    alignItems: 'stretch',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      '& .MuiButton-root': {
        width: 'calc(100% - 12px)'
      }
    }
  },
  secondaryActions: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  textAction: {
    appearance: 'none',
    background: 'none',
    border: 0,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    font: 'inherit',
    padding: 0,
    textDecoration: 'underline',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2
    }
  },
  recoveryAction: {
    fontSize: theme.typography.body2.fontSize
  }
}));

export default function LoginPage() {
  let { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const auth = useAuth();
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [msg, setMsg] = useState('');
  let { from } = location.state || { from: { pathname: '/' } };

  useEffect(() => {
    if (auth.user) {
      history.replace(from);
    }
  }, [auth]);

  const login = (event) => {
    event.preventDefault();
    // Browsers can restore/autofill inputs without firing React change events.
    // Read the actual fields at submission, including when hidden for 2FA.
    const form = new FormData(event.currentTarget);
    auth.signin(form.get('username'), form.get('password'), otp || null, (m) => {
      if (m === 'good') {
        // Preserve the destination without mounting it before the reload
        window.history.replaceState(window.history.state, '', history.createHref(from));
        // Hash-route navigation alone keeps the old GUI bundle loaded.
        window.location.reload();
        return
      }
      setMsg(m);
    }, true);
  };

  const [open_reset_dialog, setOpenResetDialog] = React.useState(false);
  const openResetDialog = () => {
    setOpenResetDialog(true);
  };
  const closeResetDialog = () => {
    setOpenResetDialog(false);
  };
  const [open_2fa_recover_dialog, setOpen2FARecoverDialog] = React.useState(false);

  const classes = useStyles();

  return (
    <Container maxWidth="sm">
      <Paper mt={4} component={Box}>
        <ResetDialog open={open_reset_dialog} handleClose={closeResetDialog} />
        <Recover2FADialog
          open={open_2fa_recover_dialog}
          handleClose={() => setOpen2FARecoverDialog(false)}
          setMsg={setMsg}
        />
        <form onSubmit={login} className={classes.root}>
          <div
            style={{
              display: !['_2fa_required', 'login failed: 402'].includes(msg) ? 'block' : 'none'
            }}
          >
            <TextField
              autoFocus
              id="username"
              name="username"
              autoComplete="username"
              label={t('username')}
              type="text"
              fullWidth
            />
            <TextField
              id="password"
              name="password"
              autoComplete="current-password"
              label={t('password')}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      aria-label={t(showPassword ? 'hide_password' : 'show_password')}
                      onClick={() => setShowPassword((show) => !show)}
                      onMouseDown={(event) => event.preventDefault()}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </div>

          {['_2fa_required', 'login failed: 402'].includes(msg) ? (
            <>
              <TotpInput
                otp={otp}
                saveOtp={(event) => setOtp(event.target.value)}
                err={msg == 'login failed: 402'}
              />
            </>
          ) : (
            <>
              <Box mt={2} display="flex" color={statusColors['bad']}>
                {msg}
              </Box>
            </>
          )}
          <Box mt={2} className={classes.loginActions}>
            <Button variant="contained" type="submit" color="primary">
              {t('login')}
            </Button>
          </Box>
          <Box mt={2} className={classes.secondaryActions}>
            {['_2fa_required', 'login failed: 402'].includes(msg) && (
              <button
                type="button"
                className={`${classes.textAction} ${classes.recoveryAction}`}
                onClick={() => setOpen2FARecoverDialog(true)}
              >
                {t('_2fa_recovery')}
              </button>
            )}
            <button type="button" className={classes.textAction} onClick={openResetDialog}>
              {t('reset_password')}
            </button>
            <Link style={{ color: statusColors.good }} to="/register">
              {t('to_register')}
            </Link>
          </Box>
        </form>
      </Paper>
    </Container>
  );
}

function ResetDialog({ open, handleClose }) {
  const [username, setUsername] = React.useState(null);
  let { t } = useTranslation();

  const validateForm = (event) => {
    if (username) {
      var json_request = {};
      json_request['username'] = username;

      fetch('/api/reset_password', { method: 'POST', body: JSON.stringify(json_request) }).then(
        (response) => {
          if (response.ok) {
            alert('You received a mail with a password-reset-link.');
          } else if (response.status === 401) {
            alert(
              t('r_pw_err_unathenticated_1') + ' ' + username + ' ' + t('r_pw_err_unathenticated_2')
            );
          } else if (response.status === 400) {
            alert(t('r_pw_err_no_email_1') + ' ' + username + ' ' + t('r_pw_err_no_email_2'));
          } else if (response.status === 500) {
            alert(t('r_pw_err_server_error'));
          }
        }
      );
      handleClose();
    } else {
      handleClose();
    }
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={validateForm}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('reset_password')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="serial"
          label={t('username')}
          type="text"
          fullWidth
          onChange={(event) => setUsername(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Button onClick={validateForm} color="primary">
          {t('reset')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function Recover2FADialog({ open, handleClose, setMsg }) {
  const [username, setUsername] = React.useState(null);
  const [password, setPassword] = React.useState(null);
  const [recovery, setRecovery] = React.useState(null);
  let { t } = useTranslation();

  const validateForm = (event) => {
    fetch('/api/recover_2fa', {
      method: 'POST',
      body: JSON.stringify({ username: username, password: password, recovery: recovery })
    }).then((response) => {
      if (response.ok) {
        alert(t('_2fa_recovery_success'));
        setMsg('');
        handleClose();
      } else {
        alert(t('_2fa_recovery_invalid'));
      }
    });
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={validateForm}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('_2fa_recovery')}</DialogTitle>
      <DialogContent>
        <span> {t('_2fa_recovery_instructions')}</span>

        <TextField
          autoFocus
          margin="dense"
          id="serial"
          label={t('username')}
          type="text"
          fullWidth
          onChange={(event) => setUsername(event.target.value)}
        />
        <TextField
          autoFocus
          margin="dense"
          id="serial"
          label={t('password')}
          type="password"
          fullWidth
          onChange={(event) => setPassword(event.target.value)}
        />
        <TextField
          autoFocus
          margin="dense"
          id="serial"
          label={t('_2fa_recovery_key')}
          type="text"
          fullWidth
          onChange={(event) => setRecovery(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Button onClick={validateForm} color="primary">
          {t('reset')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}
